// Game-side diagnostic logging that flows back to the server over the
// existing WebSocket. Iteration 1.10.
//
// Usage:
//   import { log, configureLog, setLogLevel } from './log.js'
//   configureLog({ socket: ws, defaultLevel: 'error' })
//   log.debug('hello', { details })
//
// Verbosity is server-controlled: the server pushes
// `{kind:"set_log_mode", level}` over the WebSocket and the game should
// call setLogLevel(level) in response. There is no URL flag and no
// browser-side configuration knob — production default is 'error'.

const LEVELS = Object.freeze({ debug: 0, info: 1, warn: 2, error: 3 })
const PRODUCTION_LEVEL = 'error'

// Token-bucket rate limit: 100 msgs/sec per game. Excess messages are
// dropped silently after one console.warn per drop window so the dev sees
// they're losing data without flooding their devtools too.
const TOKEN_RATE = 100
const TOKEN_BURST = 100

const state = {
  socket: null,
  threshold: LEVELS[PRODUCTION_LEVEL],
  tokens: TOKEN_BURST,
  lastRefillMs: 0,
  dropWarned: false,
}

export function configureLog(opts) {
  state.socket = opts?.socket ?? null
  const level = opts?.defaultLevel ?? PRODUCTION_LEVEL
  setLogLevel(level)
  state.tokens = TOKEN_BURST
  state.lastRefillMs = nowMs()
  state.dropWarned = false
}

// setLogLevel updates the threshold. Called from main.js when a
// `set_log_mode` WebSocket frame arrives. Unknown levels are ignored.
export function setLogLevel(name) {
  if (typeof name !== 'string') return
  if (!Object.prototype.hasOwnProperty.call(LEVELS, name)) return
  state.threshold = LEVELS[name]
}

export const log = {
  debug: (message, context) => emit('debug', message, context),
  info: (message, context) => emit('info', message, context),
  warn: (message, context) => emit('warn', message, context),
  error: (message, context) => emit('error', message, context),
}

function emit(level, message, context) {
  if (LEVELS[level] < state.threshold) return
  if (!takeToken()) {
    if (!state.dropWarned) {
      state.dropWarned = true
      // eslint-disable-next-line no-console
      console.warn('game log: rate-limit exceeded; dropping log emissions')
    }
    return
  }
  const ws = state.socket
  if (!ws || ws.readyState !== 1 /* OPEN */) return
  const payload = { kind: 'log', level, ts: gameFrame(), message: String(message) }
  if (context !== undefined) payload.context = context
  try {
    ws.send(JSON.stringify(payload))
  } catch {
    // socket may be closing; drop silently
  }
}

function takeToken() {
  const now = nowMs()
  const elapsed = Math.max(0, now - state.lastRefillMs)
  if (elapsed > 0) {
    state.tokens = Math.min(TOKEN_BURST, state.tokens + (elapsed * TOKEN_RATE) / 1000)
    state.lastRefillMs = now
  }
  if (state.tokens >= 1) {
    state.tokens -= 1
    if (state.tokens >= 1) state.dropWarned = false
    return true
  }
  return false
}

function nowMs() {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now()
  }
  return Date.now()
}

// gameFrame is a coarse millisecond stamp used as the `ts` field on the
// wire so server-side log lines can be correlated with packet arrivals.
// Anchored at module load so values stay small and JSON-friendly.
const moduleStartMs = nowMs()
function gameFrame() {
  return Math.floor(nowMs() - moduleStartMs)
}
